import { EXAM_COURSE_ROUTES } from "./examCourseRoutes";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DOMPurify from "dompurify";
import "./CourseTab.css";
import "../DashboardSkeleton.css";
import { useExamReviews } from "../../hooks/useExamReviews";
import { useExamCourseCategories } from "../../hooks/useExamCourseCategories";

import courseImage from "../../assets/images/c1.png";

const SkeletonLine = ({ className = "" }) => (
  <span className={`course-tab-skeleton__line dashboard-skeleton ${className}`} />
);

function toApiColor(value) {
  const color = String(value || '').trim().replace(/^#/, '');
  return /^[0-9a-f]{3,8}$/i.test(color) ? `#${color}` : undefined;
}

function OverviewShimmer() {
  return (
    <div className="course-tab-skeleton course-tab-skeleton--overview" aria-label="Loading course overview">
      <div className="course-tab-skeleton__hero">
        <div className="course-tab-skeleton__stack">
          <SkeletonLine className="course-tab-skeleton__line--hero-title" />
          <SkeletonLine className="course-tab-skeleton__line--hero-tag" />
          <SkeletonLine className="course-tab-skeleton__line--hero-language" />
        </div>
        <span className="course-tab-skeleton__thumbnail dashboard-skeleton" />
      </div>
      <div className="course-tab-skeleton__overview-heading">
        <span className="course-tab-skeleton__overview-icon dashboard-skeleton" />
        <SkeletonLine className="course-tab-skeleton__line--heading" />
      </div>
      <div className="course-tab-skeleton__stack course-tab-skeleton__stack--copy">
        <SkeletonLine />
        <SkeletonLine className="course-tab-skeleton__line--wide" />
        <SkeletonLine className="course-tab-skeleton__line--medium" />
        <SkeletonLine className="course-tab-skeleton__line--wide" />
      </div>
    </div>
  );
}

function CategoryShimmer() {
  return (
    <div className="course-tab-skeleton__category-grid" aria-label="Loading content categories">
      {Array.from({ length: 6 }).map((_, index) => (
        <div key={index} className="course-tab-skeleton__category-card">
          <span className="course-tab-skeleton__category-image dashboard-skeleton" />
          <div className="course-tab-skeleton__stack">
            <SkeletonLine className="course-tab-skeleton__line--category-title" />
            <SkeletonLine className="course-tab-skeleton__line--category-copy" />
            <SkeletonLine className="course-tab-skeleton__line--category-copy-short" />
          </div>
        </div>
      ))}
    </div>
  );
}

function ReviewsShimmer() {
  return (
    <div className="course-tab-skeleton__reviews" aria-label="Loading student reviews">
      <div className="course-tab-skeleton__review-list">
        <div className="course-tab-skeleton__review-summary">
          <SkeletonLine className="course-tab-skeleton__line--rating" />
          <div className="course-tab-skeleton__stack">
            <SkeletonLine className="course-tab-skeleton__line--stars" />
            <SkeletonLine className="course-tab-skeleton__line--review-summary" />
          </div>
        </div>
        {Array.from({ length: 4 }).map((_, index) => (
          <div key={index} className="course-tab-skeleton__review-item">
            <SkeletonLine className="course-tab-skeleton__line--stars" />
            <SkeletonLine className="course-tab-skeleton__line--wide" />
            <SkeletonLine className="course-tab-skeleton__line--medium" />
            <div className="course-tab-skeleton__review-user">
              <span className="course-tab-skeleton__avatar dashboard-skeleton" />
              <SkeletonLine className="course-tab-skeleton__line--user" />
            </div>
          </div>
        ))}
      </div>
      <div className="course-tab-skeleton__review-form">
        <SkeletonLine className="course-tab-skeleton__line--form-heading" />
        <SkeletonLine className="course-tab-skeleton__line--label" />
        <SkeletonLine className="course-tab-skeleton__line--stars" />
        <SkeletonLine className="course-tab-skeleton__line--label" />
        <span className="course-tab-skeleton__textarea dashboard-skeleton" />
        <SkeletonLine className="course-tab-skeleton__line--form-action" />
      </div>
    </div>
  );
}

function getStoredActiveTab(examsId) {
  if (typeof window === 'undefined' || !Number(examsId)) return 'overview';

  const savedTab = window.sessionStorage.getItem(`vidya-kasam:exam-course:${examsId}:active-tab`);
  return ['overview', 'demo', 'course', 'reviews'].includes(savedTab) ? savedTab : 'overview';
}

const CourseTabs = ({ overview = {}, isOverviewLoading = false, overviewError = "", examsId = 0 }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(() => getStoredActiveTab(examsId));

  useEffect(() => {
    setActiveTab(getStoredActiveTab(examsId));
  }, [examsId]);

  const [selectedRating, setSelectedRating] = useState(1);
  const [reviewText, setReviewText] = useState("");
  const {
    data: reviewsData = {},
    isLoading: isReviewsLoading,
    isError: hasReviewsError,
    error: reviewsError,
    postReviewMutation,
  } = useExamReviews(examsId, activeTab === "reviews");
  const {
    data: courseCategoriesData = {},
    isLoading: isCourseCategoriesLoading,
    isError: hasCourseCategoriesError,
    error: courseCategoriesError,
  } = useExamCourseCategories(examsId, activeTab === "demo" || activeTab === "course");


  const tabs = [
    { id: "overview", label: "Overview" },
    { id: "demo", label: "Demo Content" },
    { id: "course", label: "Course Content" },
    { id: "reviews", label: "Student Reviews" },
  ];

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);

    if (typeof window !== 'undefined' && Number(examsId)) {
      window.sessionStorage.setItem(`vidya-kasam:exam-course:${examsId}:active-tab`, tabId);
    }
  };

  const courseCategories = Array.isArray(courseCategoriesData?.category_list)
    ? courseCategoriesData.category_list
    : [];
  const categoryThemes = ["cyan", "purple", "yellow", "light-pink", "blue", "lavender", "soft-pink", "rose", "green"];

  const resourceTypesByCategoryId = {
    1: 'quiz',
    2: 'question-bank',
    3: 'pyp',
    4: 'short-notes',
    5: 'rapid-fire',
    9: 'flash-cards',
    12: 'audio-podcast',
    13: 'infographics',
  };

  const resolveResourceType = (item) =>
    resourceTypesByCategoryId[Number(item?.category_id || item?.id || 0)] || null;

  const renderCategories = (showProBadge = false) => {
    if (isCourseCategoriesLoading) {
      return <CategoryShimmer />;
    }

    if (hasCourseCategoriesError) {
      return <p className="course-tab-status">{courseCategoriesError?.response?.data?.message || courseCategoriesError?.message || "Unable to load categories."}</p>;
    }

    if (!courseCategories.length) {
      return <p className="course-tab-status">No content categories are available for this exam.</p>;
    }

    return courseCategories.map((item, index) => {
      const theme = categoryThemes[index % categoryThemes.length];
      const title = item?.category_name || "Course category";
      const resourceType = resolveResourceType(item);
      const backgroundColor = toApiColor(item?.background_color_code);
      const borderColor = toApiColor(item?.border_color_code);

      return (
        <button
          type="button"
          key={`${item?.route_key || item?.category_id || index}-${index}`}
          className={`category-module-card category-module-card--${theme}`}
          style={{
            ...(backgroundColor ? { background: backgroundColor } : {}),
            ...(borderColor ? { borderColor } : {}),
          }}
          aria-label={`Open ${title}`}
          disabled={!resourceType}
          onClick={() => {
            if (!resourceType) return;
            const subjectResource = ['question-bank', 'lecture', 'short-notes', 'rapid-fire', 'flash-cards', 'audio-podcast', 'infographics'].includes(resourceType);
            navigate(subjectResource ? EXAM_COURSE_ROUTES.subjects : `${EXAM_COURSE_ROUTES.resources}/${resourceType}`, {
              state: {
                examFlow: subjectResource,
                exams_id: Number(examsId),
                category_id: Number(item?.category_id || item?.id || 0),
                subject_category_id: Number(item?.subject_category_id || 0) || undefined,
                exam_name: overview?.exam_name,
                free_paid: showProBadge ? 'P' : 'F',
                plan_duration_id: Number(
                  overview?.exam_plan?.pricing_duration_id ||
                  overview?.exam_plan?.pricingDurationId ||
                  0
                ),
                exam_plan_id: Number(
                  overview?.exam_plan?.plan_id ||
                  overview?.exam_plan?.planId ||
                  0
                ),
                is_mock: item?.is_mock,
              },
            });
          }}
        >
          {showProBadge ? <span className="category-module-card__pro">PRO</span> : null}
          <span className="category-module-card__image-wrapper">
            <img src={item?.category_image || courseImage} alt="" className="category-module-card__image" />
          </span>
          <span className="category-module-card__content">
            <span className="category-module-card__title">{title}</span>
            <span className="category-module-card__description">{item?.description || ""}</span>
          </span>
        </button>
      );
    });
  };


  const reviews = (reviewsData?.reviews || reviewsData?.review_list || reviewsData?.data || []).map((review, index) => {
    const name = review?.user_name || review?.name || review?.username || "Student";
    return {
      id: review?.review_id || review?.id || index,
      rating: Number(review?.rating || review?.score || 0),
      name,
      initial: name.charAt(0).toUpperCase(),
      message: review?.review || review?.message || review?.comment || "",
    };
  });
  const averageRating = Number(reviewsData?.average_rating || reviewsData?.avg_rating || 0);
  const totalReviews = Number(reviewsData?.total_reviews || reviewsData?.total_count || reviews.length);

  const handlePostReview = async () => {
    const review = reviewText.trim();
    if (!review || !Number(examsId)) return;

    await postReviewMutation.mutateAsync({ exams_id: Number(examsId), rating: selectedRating, review });
    setReviewText("");
    setSelectedRating(1);
  };

  const handleCancel = () => {
    setSelectedRating(1);
    setReviewText("");
  };

  const renderContent = () => {
    switch (activeTab) {
      case "overview":
        if (isOverviewLoading) {
          return <OverviewShimmer />;
        }

        return (
          <div className="courseTabContent">
            {/* Course Top Card */}
            <div className="courseOverviewCard">
              <div className="courseHeaderSection">
                <div className="courseDetailsSection">
                  <div className="courseTitleSection">
                    <h2>
                      {overview?.exam_name || "Course overview"}
                    </h2>
                  </div>

                  <div className="courseInfoBox">
                    {overview?.state_name || "Exam preparation course"}
                  </div>

                  <div className="courseLanguage">
                    <span className="languageIcon">
                      <i className="bi bi-translate"></i>
                    </span>

                    <span>{overview?.available_languages || "Available in English, Hindi"}</span>
                  </div>
                </div>

                <img
                  src={overview?.exam_image || courseImage}
                  alt="Course"
                  className="courseThumbnail"
                />
              </div>
            </div>

            {/* Overview Header */}
            <div className="overviewHeader">
              <div className="overviewIconBox">
                <i className="bi bi-file-earmark-text overviewIcon"></i>
              </div>

              <h2 className="overviewTitle">Overview</h2>
            </div>

            {/* Overview Text */}
            <div className="overviewDescription">
              {overviewError ? <p>{overviewError}</p> : null}
              {!overviewError && overview?.description ? (
                <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(overview.description) }} />
              ) : null}
              {!overviewError && !overview?.description ? (
                <p>Course overview is not available.</p>
              ) : null}
            </div>
          </div>
        );

      case "demo":
        return (
          <div className="tabDynamicContent">
            <section className="categories-module-section">
              <div className="categories-module-grid">
                {renderCategories()}
              </div>
            </section>
          </div>
        );

      case "course":
        return (
          <div className="tabDynamicContent">
            <section className="categories-module-section">
              <div className="categories-module-grid">
                {renderCategories(true)}
              </div>
            </section>
          </div>
        );

      case "reviews":
        if (isReviewsLoading) {
          return <ReviewsShimmer />;
        }

        return (
          <div className="tabDynamicContent">
            <div className="student-review-layout">

              {/* ================= LEFT REVIEW SECTION ================= */}
              <div className="student-review-module">

                {/* Rating Summary */}
                <div className="student-review-module__summary">
                  <h1 className="student-review-module__main-rating">
                    {averageRating ? averageRating.toFixed(1) : "-"}
                  </h1>

                  <div className="student-review-module__summary-content">
                    <div className="student-review-module__summary-stars">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <i
                          key={star}
                          className="fa-solid fa-star"
                        ></i>
                      ))}
                    </div>

                    <p>
                      {totalReviews ? `Rated by ${totalReviews} user${totalReviews === 1 ? "" : "s"} who used for preparation` : "No student reviews yet"}
                    </p>
                  </div>
                </div>

                <div className="student-review-module__divider" />

                {/* Review List */}
                <div className="student-review-module__list">
                  {hasReviewsError ? <p className="student-review-module__status">{reviewsError?.response?.data?.message || reviewsError?.message || "Unable to load reviews."}</p> : null}
                  {!hasReviewsError && !reviews.length ? <p className="student-review-module__status">No reviews yet. Be the first to share feedback.</p> : null}
                  {reviews.map((review) => (
                    <div
                      className="student-review-module__item"
                      key={review.id}
                    >

                      <div className="student-review-module__rating-row">
                        <span className="student-review-module__rating">
                          {review.rating}
                        </span>

                        <div className="student-review-module__stars">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <i
                              key={star}
                              className={star <= Math.round(review.rating) ? "fa-solid fa-star" : "fa-regular fa-star"}
                            ></i>
                          ))}
                        </div>
                      </div>

                      {review.message && (
                        <p className="student-review-module__message">
                          {review.message}
                        </p>
                      )}

                      <div className="student-review-module__user">
                        <div className="student-review-module__avatar">
                          {review.initial}
                        </div>

                        <span className="student-review-module__name">
                          {review.name}
                        </span>
                      </div>

                    </div>
                  ))}
                </div>

              </div>

              {/* ================= RIGHT WRITE REVIEW SECTION ================= */}
              <div className="write-review-module">

                <div className="write-review-module__header">
                  Write a Review
                </div>

                <div className="write-review-module__body">

                  {/* Score */}
                  <div className="write-review-module__label">
                    Score:
                  </div>

                  <div className="write-review-module__stars">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        type="button"
                        key={star}
                        className={`write-review-module__star ${star <= selectedRating
                            ? "write-review-module__star--active"
                            : ""
                          }`}
                        onClick={() => setSelectedRating(star)}
                        disabled={postReviewMutation.isPending || !Number(examsId)}
                      >
                        <i
                          className={
                            star <= selectedRating
                              ? "fa-solid fa-star"
                              : "fa-regular fa-star"
                          }
                        ></i>
                      </button>
                    ))}
                  </div>

                  {/* Review Label */}
                  <div className="write-review-module__label review-label">
                    Review:
                  </div>

                  {/* Textarea */}
                  <textarea
                    className="write-review-module__textarea"
                    placeholder="Write Your Review..."
                    value={reviewText}
                    onChange={(e) => setReviewText(e.target.value)}
                  />
                  {postReviewMutation.isError ? (
                    <p className="student-review-module__status">
                      {postReviewMutation.error?.response?.data?.message || postReviewMutation.error?.message || "Unable to post review."}
                    </p>
                  ) : null}

                  {/* Buttons */}
                  <div className="write-review-module__actions">

                    <button
                      type="button"
                      className="write-review-module__cancel"
                      onClick={handleCancel}
                    >
                      Cancel
                    </button>

                    <button
                      type="button"
                      className="write-review-module__post"
                      onClick={handlePostReview}
                      disabled={postReviewMutation.isPending || !reviewText.trim() || !Number(examsId)}
                    >
                      {postReviewMutation.isPending ? "Posting..." : "Post"}
                    </button>

                  </div>

                </div>

              </div>

            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="courseTabsWrapper">

      {/* ================= TABS HEADER ================= */}
      <div className="courseTabsHeader">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`courseTabButton ${activeTab === tab.id ? "activeCourseTab" : ""
              }`}
            onClick={() => handleTabChange(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ================= SCROLLABLE TAB CONTENT ================= */}
      <div className="courseTabBody">
        {renderContent()}
      </div>

      {/* ================= COMMON FIXED BOTTOM FOOTER ================= */}
      <div className="courseCommonFooter">

        <div className="offerStrip">
          <span>
            <i className="fa-solid fa-fire"></i>
            {" "}Hurry!! Today's Price 93% OFF
          </span>

          <span className="offerBolt">
            <i className="fa-solid fa-bolt"></i>
          </span>
        </div>

        <div className="coursePriceSection">
          <div className="coursePrice">
            <span className="startText">
              Start @ just
            </span>

            <span className="currentPrice">
              ₹ 499.00
            </span>

            <span className="oldPrice">
              ₹ 699.00
            </span>
          </div>

          <button type="button" className="buyNowButton">
            Buy Now
          </button>
        </div>

      </div>
    </div>
  );
};

export default CourseTabs;
